// Searchable multi-select with chips — Polish B2 follow-up.
//
// Mirrors the Mantine ``MultiSelect`` UX without pulling in the
// library: pills inside the input show selected items, type to
// search, click an option to add it, click ``×`` on a pill (or
// backspace in the empty input) to remove. Dropdown closes on
// blur / outside click. Keyboard arrow-down/up navigate options;
// Enter selects.

import { ChevronDown, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";


export interface MultiSelectOption {
  value: string;
  label: string;
}


export interface MultiSelectProps {
  options: MultiSelectOption[];
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  searchable?: boolean;
  /** Test id base; the trigger gets ``-trigger``, the listbox
   * ``-listbox``, each option ``-option-<value>``. */
  testId: string;
  /** Optional empty-state copy in the listbox. */
  nothingFoundMessage?: string;
}


export function MultiSelect({
  options, value, onChange,
  placeholder = "Select…",
  searchable = true,
  testId,
  nothingFoundMessage = "Nothing found",
}: MultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Close on outside click.
  // Capture-phase so the read of ``event.target`` happens before
  // React's bubble-phase handler rebuilds the listbox (otherwise the
  // clicked option is detached by the time we check ``contains``,
  // and the dropdown closes spuriously).
  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      const node = containerRef.current;
      if (
        node && e.target instanceof Node && !node.contains(e.target)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handler, true);
    return () => document.removeEventListener("mousedown", handler, true);
  }, [open]);

  const labelByValue = useMemo(() => {
    const map = new Map<string, string>();
    for (const o of options) map.set(o.value, o.label);
    return map;
  }, [options]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const list = options.filter((o) => !value.includes(o.value));
    if (needle.length === 0) return list;
    return list.filter((o) => o.label.toLowerCase().includes(needle));
  }, [options, value, query]);

  // Keep activeIndex in bounds when filter changes.
  useEffect(() => {
    if (activeIndex >= filtered.length) setActiveIndex(0);
  }, [activeIndex, filtered.length]);

  function add(val: string): void {
    if (!value.includes(val)) onChange([...value, val]);
    setQuery("");
    setActiveIndex(0);
    inputRef.current?.focus();
  }

  function remove(val: string): void {
    onChange(value.filter((v) => v !== val));
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void {
    if (e.key === "Backspace" && query === "" && value.length > 0) {
      remove(value[value.length - 1]);
      return;
    }
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "Enter") {
        setOpen(true);
        e.preventDefault();
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(filtered.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const opt = filtered[activeIndex];
      if (opt) add(opt.value);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  return (
    <div ref={containerRef} className="relative min-w-[12rem]">
      <div
        data-testid={`${testId}-trigger`}
        className={[
          "flex flex-wrap items-center gap-1 rounded-md border bg-raised px-2 py-1 text-xs",
          open ? "border-teal" : "border-border-subtle",
        ].join(" ")}
        onClick={() => {
          setOpen(true);
          inputRef.current?.focus();
        }}
      >
        {value.length > 0 && (
          <span
            data-testid={`${testId}-count`}
            className="rounded-full bg-recessed px-1.5 py-0.5 text-[10px] font-medium text-text-tertiary"
          >
            {value.length}
          </span>
        )}
        {value.map((v) => (
          <span
            key={v}
            data-testid={`${testId}-pill-${v}`}
            className="inline-flex items-center gap-1 rounded-md bg-sage-tint px-1.5 py-0.5 text-[11px] text-teal"
          >
            {labelByValue.get(v) ?? v}
            <button
              type="button"
              aria-label={`Remove ${labelByValue.get(v) ?? v}`}
              data-testid={`${testId}-pill-remove-${v}`}
              onClick={(e) => {
                e.stopPropagation();
                remove(v);
              }}
              className="text-teal/70 hover:text-teal"
            >
              <X size={10} aria-hidden="true" />
            </button>
          </span>
        ))}
        {searchable ? (
          <input
            ref={inputRef}
            type="text"
            data-testid={`${testId}-input`}
            value={query}
            placeholder={value.length === 0 ? placeholder : ""}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
              setActiveIndex(0);
            }}
            onKeyDown={handleKeyDown}
            onFocus={() => setOpen(true)}
            className="min-w-[4rem] flex-1 bg-transparent text-text-primary outline-none placeholder:text-text-tertiary"
          />
        ) : (
          <span className="flex-1 text-text-tertiary">
            {value.length === 0 ? placeholder : ""}
          </span>
        )}
        <ChevronDown
          size={12}
          aria-hidden="true"
          className="text-text-tertiary"
        />
      </div>
      {open && (
        <ul
          role="listbox"
          data-testid={`${testId}-listbox`}
          className="absolute left-0 top-full z-30 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-border-subtle bg-raised p-1 shadow-lg"
        >
          {filtered.length === 0 ? (
            <li
              data-testid={`${testId}-empty`}
              className="px-2 py-1.5 text-xs italic text-text-tertiary"
            >
              {nothingFoundMessage}
            </li>
          ) : (
            filtered.map((o, i) => (
              <li
                key={o.value}
                role="option"
                aria-selected={i === activeIndex}
                data-testid={`${testId}-option-${o.value}`}
                onMouseEnter={() => setActiveIndex(i)}
                onMouseDown={(e) => {
                  // ``mousedown`` so the input doesn't blur first.
                  e.preventDefault();
                  add(o.value);
                }}
                className={[
                  "cursor-pointer rounded px-2 py-1 text-xs",
                  i === activeIndex
                    ? "bg-recessed text-text-primary"
                    : "text-text-secondary",
                ].join(" ")}
              >
                {o.label}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
